import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  styled,
  Button,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion } from 'framer-motion';

const StyledPricingSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  backgroundColor: theme.palette.background.paper,
  minHeight: '100vh',
}));

const packages = [
  {
    id: 'landing',
    name: 'Landing Page',
    price: '$350–500',
    priceNote: 'Starting at',
    description: 'One-page responsive landing to capture leads and showcase your business.',
    features: [
      'Custom responsive design (desktop/tablet/mobile)',
      'Contact form + basic lead capture',
      '1 content section + hero and CTA',
      'Deployment to Vercel/Netlify',
      '1 week post-launch support',
    ],
    recommended: false,
    timeline: '1–3 days',
  },
  {
    id: 'business',
    name: 'Business Site',
    price: '$900',
    priceNote: 'Starting at',
    description: 'Multi-page business website with CMS option and SEO basics.',
    features: [
      'Up to 5 pages (Home, About, Services, Contact, Blog)',
      'Custom UI/UX and responsive layout',
      'Basic SEO & performance optimization',
      'Contact forms, map, and social links',
      '2 weeks post-launch support',
    ],
    recommended: true,
    timeline: '1–2 weeks',
  },
  {
    id: 'ecom',
    name: 'E-commerce',
    price: '$2,000',
    priceNote: 'Starting at',
    description: 'Full-featured e-commerce site with payment integration and admin dashboard.',
    features: [
      'Product listing & cart checkout (Stripe/PayPal)',
      'Admin product management (basic)',
      'Order email notifications',
      'Performance & security best-practices',
      '4 weeks post-launch support',
    ],
    recommended: false,
    timeline: '3–6 weeks',
  },
];

const addOns = [
  { label: 'Extra page', price: '$75 / page' },
  { label: 'SEO package (on-page)', price: '$200' },
  { label: 'CMS setup (WordPress / Netlify CMS)', price: '$300' },
  { label: 'Monthly maintenance', price: '$60 / month' },
  { label: 'Priority turnaround', price: ' +20% (depending on timeline)' },
];

const faqs = [
  {
    q: 'How do you price custom projects?',
    a: 'Custom projects are quoted after a short discovery call. I provide clear deliverables, milestones, and a deposit schedule.',
  },
  {
    q: 'Do you require a deposit?',
    a: 'Yes — typically a 30% deposit to start, 40% on milestone, and 30% on final delivery (flexible per project).',
  },
  {
    q: 'Can you update an existing website?',
    a: 'Absolutely — I offer redesigns, performance improvements, and migrations. I can provide a free audit on request.',
  },
];

const sectionVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeInOut" } },
};

const PricingPage = () => {
  return (
    <StyledPricingSection>
      <Container maxWidth="lg">
        {/* Back to Main Page Button */}
        <Box sx={{ mb: 4, textAlign: 'left' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            href="/"
          >
            Back to Main Page
          </Button>
        </Box>

        <Typography variant="h3" component="h1" align="center" fontWeight="bold" gutterBottom>
          Pricing & Services
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" paragraph>
          Packages start at the prices below. Final cost depends on scope, integrations, and custom requirements. I’ll always provide a clear quote before work begins.
        </Typography>

        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {packages.map((pkg) => (
              <Grid item xs={12} md={4} key={pkg.id}>
                <Card
                  elevation={pkg.recommended ? 8 : 2}
                  sx={{
                    borderRadius: 3,
                    ...(pkg.recommended && { border: '2px solid', borderColor: 'primary.main' }),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" fontWeight="bold">
                        {pkg.name}
                      </Typography>
                      {pkg.recommended && <StarIcon color="primary" />}
                    </Box>
                    <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
                      {pkg.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {pkg.description}
                    </Typography>
                    <List dense>
                      {pkg.features.map((f, i) => (
                        <ListItem key={i} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText primary={f} />
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Timeline: <strong>{pkg.timeline}</strong>
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      variant={pkg.recommended ? 'contained' : 'outlined'}
                      color="secondary"
                      href={`mailto:CloudXviiDevelopment@outlook.com?subject=${encodeURIComponent(
                        `${pkg.name} Inquiry`
                      )}`}
                    >
                      Get a Quote
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Box sx={{ mt: 6, p: 4, border: 1, borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Optional Add-Ons
            </Typography>
            <Grid container spacing={2}>
              {addOns.map((a, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                    <Typography>{a.label}</Typography>
                    <Typography fontWeight="bold">{a.price}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Note: All prices are starting points and may vary depending on integrations (e.g., custom APIs, payment gateways) and scope. I’ll provide a detailed quote after a short discovery call.
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Frequently Asked Questions
            </Typography>
            {faqs.map((f, i) => (
              <Accordion key={i} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </motion.div>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="contained"
            size="large"
            color="secondary"
            href="mailto:CloudXviiDevelopment@outlook.com"
          >
            Request a Free Consultation
          </Button>
        </Box>
      </Container>
    </StyledPricingSection>
  );
};

export default PricingPage;